import { CHROMA_MAX_OPACITY } from 'constant'
import * as xmlEscape from 'xml-escape'
import { MEDIA_TYPE, TRANSITIONS, TRANSITION_MOUNT_TYPE } from '~/enums'
import { XTL_TRANSITION_TYPES, TRANSITION_TRACK_TYPE, CLIP_FLAGS, TRACK_TYPE } from './enums'
import { getAudioMix } from './getAudioMix'
import { num, cond } from './utils'

const ALPHA_MAX = 255
const ALPHA_MIN = 0

const BLUR_MAX = 1.0
const BLUR_MIN = 0.5

/**
 * @param {number} alphaStart
 * @param {number} alphaStop
 * @param {number} duration
 * @param {string} color
 * @returns {string}
 */
function getTransparencyEffectParams(alphaStart, alphaStop, duration, color = 'black') {
  return `
    <param name="bkcolor" value="${color}"/>
    <param name="alpha" value="${num(alphaStart)}">
      <linear
        length="${duration}"
        value="${num(alphaStop)}"
        timeFormat="100ns_units"
      />
    </param>
  `
}

/**
 * @param {TRANSITION_TRACK_TYPE} trackType
 * @param {number} duration
 * @returns {string}
 */
function getBlurEffectParams(trackType, duration, transition) {
  const originalTrack = trackType === TRANSITION_TRACK_TYPE.ORIGINAL
  let blurStop = originalTrack ? BLUR_MAX : BLUR_MIN
  let blurStart = originalTrack ? BLUR_MIN : BLUR_MAX
  if (transition.isMountIn) {
    blurStart = BLUR_MAX
    blurStop = BLUR_MIN
  }
  return `
    <param name="sigma" value="${blurStart}">
      <linear
        length="${duration}"
        value="${blurStop}"
        timeFormat="100ns_units"
      />
    </param>
  `
}

/**
 * @param {TRANSITIONS} type
 * @param {number} duration
 * @returns {string}
 */
function getWipeEffectParams(type, tr, duration) {
  let params = {
    direction: '',
    from: (tr.isMountBetween || tr.isMountIn) ? '0.0' : '1.0',
    to: (tr.isMountBetween || tr.isMountIn) ? '1.0' : '0.0',
  }
  switch (type) {
    case TRANSITIONS.WIPE_UP:
      params = {
        ...params,
        direction: (tr.isMountBetween || tr.isMountIn) ? 'top' : 'bottom',
      }
      break
    case TRANSITIONS.WIPE_DOWN:
      params = {
        ...params,
        direction: (tr.isMountBetween || tr.isMountIn) ? 'bottom' : 'top',
      }
      break
    case TRANSITIONS.WIPE_LEFT:
      params = {
        ...params,
        direction: (tr.isMountBetween || tr.isMountIn) ? 'left' : 'right',
      }
      break
    case TRANSITIONS.WIPE_RIGHT:
      params = {
        ...params,
        direction: (tr.isMountBetween || tr.isMountIn) ? 'right' : 'left',
      }
      break
    default:
      params = { ...params }
  }
  return `
    <param name="direction" value="${params.direction}"/>
    <param name="position" value="${params.from}">
        <linear length="${duration}" value="${params.to}"/>
    </param>
  `
}

/**
 * @param {TRANSITION_TRACK_TYPE} trackType
 * @param {number} duration
 * @returns {string}
 */
function getTwoSideTransitionEffectParams(trackType, duration) {
  const originalTrack = trackType === TRANSITION_TRACK_TYPE.ORIGINAL
  const alphaStart = originalTrack ? ALPHA_MAX : ALPHA_MIN
  const alphaStop = originalTrack ? ALPHA_MIN : ALPHA_MAX
  return getTransparencyEffectParams(alphaStart, alphaStop, duration)
}

/**
 * @param {TRANSITION_TRACK_TYPE} transitionType
 * @param {number} duration
 * @returns {string}
 */
function getFadeEffectParams(duration, transition) {
  const mountOut = transition.mountType === TRANSITION_MOUNT_TYPE.OUT
  const alphaStart = mountOut ? ALPHA_MAX : ALPHA_MIN
  const alphaStop = mountOut ? ALPHA_MIN : ALPHA_MAX
  return getTransparencyEffectParams(alphaStart, alphaStop, duration)
}

/**
 * @param {TRANSITION_TRACK_TYPE} trackType
 * @param {TRANSITIONS} transitionType
 * @param {number} duration
 * @returns {string}
 */
function getTransitionEffectParams(trackType, type, transition, duration) {
  if (transition.isMountBetween) {
    if ([ TRANSITIONS.BLUR, TRANSITIONS.DISSOLVE ].includes(type)) {
      return getTwoSideTransitionEffectParams(trackType, duration)
    }
  } else if (transition.isMountIn || transition.isMountOut) {
    return getFadeEffectParams(duration, transition)
  }
  return ''
}

/**
 * @param {Assets.TransitionAsset} transition
 * @param {TRANSITION_TRACK_TYPE} trackType
 * @returns {string}
 */
export function transitionEffectOnTrack(transition, trackType) {
  const { type, duration } = transition
  const effectParamsXTL = getTransitionEffectParams(trackType, type, transition, duration)
  switch (transition.type) {
    case TRANSITIONS.BLUR:
      return `
        <effect
          type="transparency"
          start="${transition.startTime}"
          stop="${transition.endTime}"
          timeFormat="100ns_units"
        >
          ${effectParamsXTL}
        </effect>
        <effect
          type="gaussianblur"
          start="${transition.startTime}"
          stop="${transition.endTime}"
          timeFormat="100ns_units"
        >
          ${getBlurEffectParams(trackType, duration, transition)}
        </effect>
      `
    case TRANSITIONS.DIP_BLACK:
    case TRANSITIONS.DIP_WHITE: {
      const color = transition.type === TRANSITIONS.DIP_WHITE ? 'white' : 'black'
      // Odd durations cannot be split in half without a fractional tick, so the
      // remainder goes to the second half. Both halves together still cover exactly
      // `startTime..endTime`, with no gap and no overlap.
      const firstHalf = Math.floor(duration / 2)
      const midTime = transition.startTime + firstHalf
      return `
        <effect
          type="transparency"
          start="${transition.startTime}"
          stop="${midTime}"
          timeFormat="100ns_units"
        >
          ${getTransparencyEffectParams(ALPHA_MAX, ALPHA_MIN, firstHalf, color)}
        </effect>
        <effect
          type="transparency"
          start="${midTime}"
          stop="${transition.endTime}"
          timeFormat="100ns_units"
        >
          ${getTransparencyEffectParams(ALPHA_MIN, ALPHA_MAX, duration - firstHalf, color)}
        </effect>
      `
    }
    case TRANSITIONS.WIPE_RIGHT:
    case TRANSITIONS.WIPE_LEFT:
    case TRANSITIONS.WIPE_UP:
    case TRANSITIONS.WIPE_DOWN:
      return `
        <effect
          type="wipe"
          start="${transition.startTime}"
          stop="${transition.endTime}"
          timeFormat="100ns_units"
        >
          ${getWipeEffectParams(transition.type, transition, duration)}
        </effect>
      `
    default:
      return `
        <effect
          type="transparency"
          start="${transition.startTime}"
          stop="${transition.endTime}"
          timeFormat="100ns_units"
        >
          ${effectParamsXTL}
        </effect>
      `
  }
}

export const getChromaKeyEffectParams = settings => {
  const { smoothness, similarity, chromaKeyColor, spillReduction, chromaKeyOpacity } = settings

  // xtl needs opacity in 0-255 diapason
  const xtlOpacity = Math.round(chromaKeyOpacity * 255 / CHROMA_MAX_OPACITY)

  return `
    <param name="r" value="${chromaKeyColor.r}" />
    <param name="g" value="${chromaKeyColor.g}" />
    <param name="b" value="${chromaKeyColor.b}" />
    <param name="similarity" value="${similarity}"/>
    <param name="smoothness" value="${smoothness}"/>
    <param name="spill" value="${spillReduction}"/>
    <param name="opacity" value="${xtlOpacity}"/>
  `
}

/**
 * @param {Assets.Asset} asset
 * @param {TRANSITION_TRACK_TYPE} trackType
 * @returns {string}
 */
export function chromaKeyEffectOnTrack(asset) {
  const { startTime, endTime } = asset
  const effectParamsXTL = getChromaKeyEffectParams(asset.settings)
  return `
    <effect
      type="chroma_key"
      start="${startTime}"
      stop="${endTime}"
      timeFormat="100ns_units"
    >
      ${effectParamsXTL}
    </effect>
  `
}

/**
 * @param {string} fileId
 * @param {number} mediaStart
 * @param {number} mediaEnd
 * @return {string}
 */
function clipWithStartStop({ fileId, mediaStart, mediaEnd }) {
  return `
    <clip
      src="${fileId}"
      start="${mediaStart}"
      stop="${mediaEnd}"
      timeFormat="100ns_units"
    />
  `
}

/**
 * @param {string} fileId
 * @param {number} duration
 * @return {string}
 */
function clipWithLength({ fileId, duration }) {
  return `
    <clip
      src="${fileId}"
      length="${duration}"
      timeFormat="100ns_units"
    />
  `
}

/**
 * @param {string} fileId
 * @param {string} clipXTL
 * @param {string} flags
 * @param {object} [padding]
 * @param {number} [padding.left=0]
 * @param {number} [padding.right=0]
 * @return {string}
 */
function paddedClip({ fileId, clipXTL, padding = {}, flags = CLIP_FLAGS.BLANK }) {
  const { left = 0, right = 0 } = padding
  const flagsAttr = flags ? `flags="${flags}"` : ''
  return `
    ${cond(left > 0, `
      <clip
        src="${fileId}"
        length="${left}"
        timeFormat="100ns_units"
        ${flagsAttr}
      />
    `)}
    ${clipXTL}
    ${cond(right > 0, `
      <clip src="${fileId}"
        length="${right}"
        timeFormat="100ns_units"
        ${flagsAttr}
      />
    `)}
  `
}
/**
 * @param {TRACK_TYPE} trackType
 * @param {MEDIA_TYPE} trackType
 * @returns {Number}
 */
function getAudioTrackNum(format) {
  return (format === MEDIA_TYPE.MXF) ? -1 : 1
}

/**
 * Fade lengths are clamped against the clip. Trimming a clip shorter than its fades
 * never re-clamps `fadeInDuration`/`fadeOutDuration` (only `TimelineItem::duration`
 * is clamped), so a stale value would otherwise push an effect window past
 * `endTime`. Clamping here too - `patchAudioTransition` does the same on patch -
 * guarantees the three windows below can neither invert nor overlap.
 *
 * @param {Assets.AudioAsset} asset
 * @return {{ fadeIn: number, fadeOut: number }}
 */
function getFadeLengths(asset) {
  const {
    volume, duration, fadeInIsActive, fadeOutIsActive, fadeInDuration, fadeOutDuration,
  } = asset

  if (!(volume > 0)) {
    return { fadeIn: 0, fadeOut: 0 }
  }

  const limit = Math.max(duration, 0)
  const fadeIn = fadeInIsActive ? Math.min(Math.max(fadeInDuration, 0), limit) : 0
  const fadeOut = fadeOutIsActive ? Math.min(Math.max(fadeOutDuration, 0), limit - fadeIn) : 0

  return { fadeIn, fadeOut }
}

/**
 * All times are integers in 100ns units (see `toFixedPointAssets`), and every
 * `<linear length>` is derived from the enclosing effect's own `stop - start`, so a
 * ramp always covers its window exactly instead of being rounded separately.
 *
 * @param {Assets.AudioAsset} [asset]
 * @return {string}
 */
export function volumeEffect(asset) {
  const { startTime, endTime, volume } = asset
  const { fadeIn, fadeOut } = getFadeLengths(asset)
  const result = []

  if (fadeIn) {
    const fadeInStop = startTime + fadeIn
    const effect = `
    <effect type="volume" start="${startTime}" stop="${fadeInStop}" timeFormat="100ns_units">
      <param name="volume" value="0">
        <linear length="${fadeInStop - startTime}" value="${num(volume * 100)}" timeFormat="100ns_units" />
      </param>
    </effect>
    `
    result.push(effect)
  }

  if (volume !== 1) {
    const steadyStart = startTime + fadeIn
    const steadyStop = endTime - fadeOut
    // Zero-length effects are rejected by the engine, and with integer times a
    // window can legitimately collapse when the fades cover the whole clip.
    if (steadyStop > steadyStart) {
      const effect = `
    <effect type="volume" start="${steadyStart}" stop="${steadyStop}" timeFormat="100ns_units">
      <param name="volume" value="${num(volume * 100)}"/>
    </effect>
    `
      result.push(effect)
    }
  }

  if (fadeOut) {
    const fadeOutStart = endTime - fadeOut
    const effect = `
    <effect type="volume" start="${fadeOutStart}" stop="${endTime}" timeFormat="100ns_units">
      <param name="volume" value="${num(volume * 100)}">
        <linear length="${endTime - fadeOutStart}" value="0" timeFormat="100ns_units"/>
      </param>
    </effect>
    `
    result.push(effect)
  }
  return result.join('\n')
}
/**
 * @param {Assets.AudioAsset} [asset]
 * @param {object} [padding]
 * @param {number} [padding.left]
 * @param {number} [padding.right]
 * @return {string}
 */
export function audioTrack({ asset, padding }) {
  const { fileId, mediaStart, mediaEnd, filetype } = asset
  const clipXTL = clipWithStartStop({ fileId, mediaStart, mediaEnd })
  const mixInterval = getAudioMix(asset)
  const effects = volumeEffect(asset)
  const clipsXTL = paddedClip({
    fileId,
    clipXTL,
    padding,
    flags: CLIP_FLAGS.AUDIO_SILENCE,
  })
  return (
    `<track audio="${getAudioTrackNum(filetype)}">
      ${clipsXTL}
      ${effects}
      ${mixInterval}
    </track>
    `
  )
}

/**
 * @param {Assets.VideoAsset} [asset]
 * @param {object} [padding]
 * @param {number} [padding.left]
 * @param {number} [padding.right]
 * @param {string} effects
 * @return {string}
 */
export function videoMixTrack({ asset, padding, effects }) {
  const { fileId, mediaStart, mediaEnd } = asset
  const clipXTL = clipWithStartStop({ fileId, mediaStart, mediaEnd })
  const clipsXTL = paddedClip({
    fileId,
    clipXTL,
    padding,
    flags: CLIP_FLAGS.BLANK,
  })
  return `
    <track video="1">
      ${clipsXTL}
      ${effects}
      <transition
        type="${XTL_TRANSITION_TYPES.MIX}"
        start="${asset.startTime}"
        stop="${asset.endTime}"
        timeFormat="100ns_units"
      >
      </transition>
    </track>
  `
}

/**
 * @param {Assets.MediaAsset} asset
 * @param {object} [padding]
 * @param {number} [padding.left]
 * @param {number} [padding.right]
 * @param {string} [effects]
 * @return {string}
 */
export function pipOverlayTrack({ asset, videoPosition, fileId, padding, effects }) {
  const { duration, mediaStart, mediaEnd } = asset
  const clipXTL = mediaStart ? clipWithStartStop({
    fileId,
    mediaStart,
    mediaEnd,
  }) : clipWithLength({ duration, fileId })
  const clipsXTL = paddedClip({
    fileId,
    clipXTL,
    padding,
  })
  return `
    <track video="1">
      ${clipsXTL}
      ${effects}
      <transition
        type="${XTL_TRANSITION_TYPES.PIP}"
        start="${asset.startTime}"
        stop="${asset.endTime}"
        timeFormat="100ns_units"
      >
        ${videoPositionParams(videoPosition)}
      </transition>
    </track>
  `
}

/**
 * @param {object} videoPosition
 * @param {number} videoPosition.x
 * @param {number} videoPosition.y
 * @param {number} videoPosition.width
 * @param {number} videoPosition.height
 */
export function videoPositionParams(videoPosition) {
  const { x, y, width, height } = videoPosition
  const params = [
    x ? { name: 'x', value: num(x, false) } : null,
    y ? { name: 'y', value: num(y, false) } : null,
    width ? { name: 'width', value: num(width, false) } : null,
    height ? { name: 'height', value: num(height, false) } : null,
  ]
  return params.map(param => param ? `<param name="${param.name}" value="${param.value}"/>` : '').join('\n')
}

/**
 * @param {Assets.TransitionAsset} asset
 * @param {object} options
 * @param {string} options.fileId
 * @param {number} options.mediaStart
 * @param {number} options.mediaEnd
 * @param {XTL_TRANSITION_TYPES} options.type
 * @param {object} [options.padding]
 * @param {number} [options.padding.left]
 * @param {number} [options.padding.right]
 * @return {string}
 */
export function transitionTrack(transition, options) {
  const { padding, fileId, mediaStart, mediaEnd, type } = options
  const clipStop = Math.min(mediaStart + transition.duration, mediaEnd)
  const clipXTL = clipWithStartStop({
    fileId,
    mediaStart,
    mediaEnd: clipStop,
  })
  const effectXTL = transitionEffectOnTrack(transition, TRANSITION_TRACK_TYPE.OVERLAY)
  const clipsXTL = paddedClip({
    fileId,
    clipXTL,
    padding,
  })
  return `
    <track video="1">
      ${clipsXTL}
      ${effectXTL}
      <transition
        type="${type}"
        start="${transition.startTime}"
        stop="${transition.endTime}"
        timeFormat="100ns_units"
      >
      </transition>
    </track>
  `
}

/**
 * @param {Assets.VideoAsset} clip
 * @returns {string}
 */
export function mediaClip(clip, flags = '') {
  const { fileId, mediaStart, mediaEnd } = clip
  if (mediaEnd - mediaStart <= 0) {
    return ''
  }
  const flagsAttr = flags ? `flags="${flags}"` : ''
  return `
    <clip
      src="${fileId}"
      start="${mediaStart}"
      stop="${mediaEnd}"
      timeFormat="100ns_units"
      ${flagsAttr}
    />
  `
}
/**
 * @param {number} duration
 * @param {TRACK_TYPE} trackType
 * @returns {string}
 */
export function blankClip(duration, trackType) {
  if (duration <= 0) { // 4463 negative not allowed
    return ''
  }
  const templateName = trackType === TRACK_TYPE.VIDEO ? 'template_blank.png' : 'template_audio_mute.mp4'
  const flags = trackType === TRACK_TYPE.VIDEO ? CLIP_FLAGS.BLANK : CLIP_FLAGS.AUDIO_SILENCE
  return `
    <clip
      src="${templateName}"
      start="0"
      stop="${duration}"
      timeFormat="100ns_units"
      flags="${flags}"
    />
  `
}

/**
 * @param {Assets.VideoAsset[]} mediaClips
 * @param {string} effects
 * @param {TRACK_TYPE} trackType
 * @returns {string}
 */
export function mediaTrack(trackType, mediaClips, effects, format, trackFlags = '') {
  const flagsAttr = trackFlags ? `flags="${trackFlags}"` : ''
  return `
    <track
      video="${(trackType === TRACK_TYPE.VIDEO || trackType === TRACK_TYPE.VIDEO_AND_AUDIO) ? 1 : 0}"
      audio="${(trackType === TRACK_TYPE.AUDIO || trackType === TRACK_TYPE.VIDEO_AND_AUDIO) ? getAudioTrackNum(format) : 0}"
      accuracy="frame" ${flagsAttr}>
      ${mediaClips}
      ${effects}
    </track>
  `
}

/**
 * @param {string} taskParams
 * @returns {string}
 */
export function taskTranscoding(taskParams) {
  return `
    <task type="transcoding">
      ${taskParams}
    </task>
`
}

const defaultMXFStreamsCount = 8

function defaultACodecParamsMXF() {
  return Array(defaultMXFStreamsCount).fill('<param name="acodec" value="type=pcm,rate=48000,channels=1,bits=16"/>').join('\n')
}

export function defaultACodecParams(format) {
  switch (format) {
    case MEDIA_TYPE.MXF:
      return defaultACodecParamsMXF()
    default:
      return '<param name="acodec" value="type=aac,profile=LC,bitrate=128000,rate=44100,channels=2"/>'
  }
}

export function defaultVCodecParams({ width, height, fpsNum, fpsDenum }) {
  return `<param name="vcodec" value="type=h264,fps_n=${fpsNum || 25},fps_d=${fpsDenum || 1},width=${width},height=${height},profile=baseline,bitrate=5000,keyint=10"/>`
}

/**
 * @param {string} referenceFileId
 * @returns {string}
 */
export function referenceFileParam(referenceFileId) {
  return `<param name="reference_file" value="${referenceFileId}"/>`
}

function format2EngineOutType(format) {
  switch (format) {
    case MEDIA_TYPE.TS:
      return 'mpg_ts'
    default:
      break
  }
  return format
}

/**
 * @param {string} outputName
 * @param {string} transcodingTask
 * @param {string} tracks
 * @returns {string}
 */
export function timelines({
  outputName,
  format,
  transcodingTask,
  tracks,
}) {
  return `
    <timelines version="3">
      <timeline>
        <group output="${xmlEscape(outputName)}.${format}" out_type="${format2EngineOutType(format)}">
          ${transcodingTask}
          ${tracks}
        </group>
      </timeline>
    </timelines>
  `
}
